<template>
  <div class="tw:flex tw:flex-col tw:gap-3 tw:w-full">
    <div v-for="(row, index) in metric.value.rows" :key="row.key ?? row.label" class="tw:w-full">
      <div class="tw:flex tw:items-baseline tw:justify-between tw:gap-3 tw:mb-0.5">
        <div class="tw:flex tw:items-baseline tw:gap-2 tw:min-w-0 tw:flex-1">
          <span v-if="metric.value.ranked" class="tw:text-xs tw:font-semibold tw:text-zinc-500 tw:tabular-nums tw:shrink-0">
            {{ index + 1 }}.
          </span>
          <Dialog v-if="opensOnto(row)">
            <DialogTrigger
              class="tw:block tw:truncate tw:min-w-0 tw:flex-1 tw:text-sm tw:text-blue-700 tw:underline tw:decoration-dotted tw:underline-offset-2 tw:text-left tw:cursor-pointer"
              :title="tooltip(row)"
            >{{ row.label }}</DialogTrigger>

            <DialogScrollContent size="lg">
              <DialogHeader>
                <DialogTitle>{{ row.label }}</DialogTitle>
              </DialogHeader>

              <div class="tw:mt-2">
                <MetricCard
                  :collection="collection"
                  :metric="metric.drilldown!.metric"
                  display="progress-bar"
                  :legend-type="legendType"
                  :filters="{ [metric.drilldown!.filter]: row.key! }"
                />
              </div>
            </DialogScrollContent>
          </Dialog>
          <span v-else class="tw:block tw:truncate tw:min-w-0 tw:flex-1 tw:text-sm tw:text-zinc-800" :title="tooltip(row)">
            {{ row.label }}
          </span>
        </div>
        <span class="tw:flex tw:items-baseline tw:gap-2 tw:shrink-0">
          <span class="tw:text-xs tw:tabular-nums tw:text-zinc-600">{{ legend(row) }}</span>
          <DeltaBadge v-if="row.delta" :delta="row.delta" />
        </span>
      </div>
      <div class="tw:w-full tw:h-2 tw:bg-zinc-100 tw:rounded-full tw:overflow-hidden" :title="row.formatted">
        <div
          class="tw:h-full tw:bg-blue-600 tw:rounded-full tw:transition-all"
          role="progressbar"
          :style="{ width: Math.round(row.percent) + '%' }"
          :aria-valuenow="Math.round(row.percent)"
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
    </div>

    <DepartedRows :departed="metric.value.departed" />
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue';
import { Dialog, DialogHeader, DialogScrollContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import DeltaBadge from './DeltaBadge.vue';
import DepartedRows from './DepartedRows.vue';
import type { DistributionMetricData, DistributionRow, MetricLegendType } from './metrics';

/** Loaded lazily: a card draws this bar chart, which may open onto a card of its own. */
const MetricCard = defineAsyncComponent(() => import('./MetricCard.vue'));

const props = withDefaults(defineProps<{
  metric: DistributionMetricData
  collection: string
  legendType?: MetricLegendType[]
}>(), {
  legendType: () => ['percentage'],
});

function opensOnto(row: DistributionRow): boolean {
  return row.key !== null && (props.metric.drilldown?.keys.includes(row.key) ?? false);
}

function tooltip(row: DistributionRow): string {
  return row.legend ? `${row.label} — ${row.legend}` : row.label;
}

function legend(row: DistributionRow): string {
  return props.legendType
    .map((type) => type === 'percentage' ? `${row.percent}%` : row.formatted)
    .join(' · ');
}
</script>
